
import React, { useState, useEffect } from 'react';
import { Search, MapPin, Briefcase, ExternalLink, Sparkles, Lock, ShieldCheck, ShieldAlert, ShieldX } from 'lucide-react';
import { searchJobs } from '../services/jobService.js';
import { generateAIContent, getPrompts } from '../aiService.js';
import { scoreJobTrust } from '../jobTrust.js';
import { useSubscription } from '../contexts/SubscriptionContext.jsx';
import AdBanner from './ui/AdBanner.jsx';
import SEO from './SEO.jsx';

import JobPortalHelper from './JobPortalHelper';

const TRUST_BADGES = {
    trusted: { Icon: ShieldCheck, bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
    caution: { Icon: ShieldAlert, bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200' },
    suspicious: { Icon: ShieldX, bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-200' }
};

const TrustBadge = ({ job }) => {
    const trust = scoreJobTrust(job);
    const cfg = TRUST_BADGES[trust.level];
    const Icon = cfg.Icon;
    return (
        <span
            className={`inline-flex items-center gap-1 ${cfg.bg} ${cfg.text} ring-1 ${cfg.ring} px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider cursor-help`}
            title={
                trust.flags.length > 0
                    ? `Trust ${trust.score}/100 — ${trust.flags.map(f => f.reason).join(' · ')}`
                    : `Trust ${trust.score}/100 — no red flags detected`
            }
        >
            <Icon size={10} /> {trust.label} · {trust.score}
        </span>
    );
};

const JobFinder = ({ resumeData, apiConfig, onBack, onAnalyzeJob }) => {
    const { isPremium, upgradeToPro } = useSubscription();
    const [viewMode, setViewMode] = useState('search'); // 'search' | 'portals'
    const [query, setQuery] = useState('');
    const [location, setLocation] = useState('');
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [autoFilling, setAutoFilling] = useState(false);

    // Initial Auto-fill from Resume (Mock or AI)
    useEffect(() => {
        // Only auto-fill if we have data and haven't searched yet
        if (resumeData && apiConfig && !query && viewMode === 'search') {
            const fill = async () => {
                autoFillSearch();
            };
            fill();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode]);

    const autoFillSearch = async () => {
        if ((!apiConfig.openai && !apiConfig.gemini && !apiConfig.anthropic)) return;

        setAutoFilling(true);
        try {
            const prompt = getPrompts('extract-search-terms', resumeData);
            const response = await generateAIContent(apiConfig, prompt);

            // Clean JSON
            const jsonStr = response.replace(/```json/g, '').replace(/```/g, '').trim();
            const data = JSON.parse(jsonStr);

            if (data.keywords) {
                // Take the first keyword
                const firstKeyword = data.keywords.split(',')[0].trim();
                setQuery(firstKeyword);
            }
            if (data.location) {
                setLocation(data.location === "Remote" ? "Remote" : data.location);
            }
        } catch (e) {
            console.error("Auto-fill failed", e);
        } finally {
            setAutoFilling(false);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!query) return;

        setLoading(true);
        setError(null);
        setJobs([]);

        try {
            const rapidKey = localStorage.getItem('rapidapi_key');
            // Allow search if we are in premium mode OR if we just want to show blurred results (mocking handled in UI)
            // But for real API call, we need the key. 
            // NOTE: If user has NO key, we can't search real jobs.
            // We'll throw error if no key.
            if (!rapidKey) {
                throw new Error("RapidAPI Key is missing. Please add it in Settings.");
            }

            const results = await searchJobs(query, location, {}, rapidKey);
            setJobs(results);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 p-6 flex flex-col h-screen overflow-hidden">
            <SEO
                title="Job Finder - Resume Architect"
                description="Find jobs that perfectly match your resume skills and experience. AI-powered job search engine."
            />
            {/* Header */}
            <div className="max-w-5xl mx-auto w-full mb-6 shrink-0">
                <button onClick={onBack} className="text-slate-500 hover:text-slate-700 mb-4 flex items-center gap-2">
                    &larr; Back to Editor
                </button>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                            <Briefcase className="text-blue-600" />
                            Job Finder
                            {!isPremium && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full uppercase tracking-wider font-bold">Premium</span>}
                        </h1>
                        <p className="text-slate-500">Find jobs relevant to your resume profile.</p>
                    </div>

                    {/* View Switcher */}
                    <div className="flex p-1 bg-slate-200 rounded-lg">
                        <button
                            onClick={() => setViewMode('search')}
                            className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${viewMode === 'search' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Search Jobs
                        </button>
                        <button
                            onClick={() => setViewMode('portals')}
                            className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${viewMode === 'portals' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Direct Upload
                        </button>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            {viewMode === 'portals' ? (
                <div className="flex-1 max-w-7xl mx-auto w-full overflow-hidden">
                    <JobPortalHelper resumeData={resumeData} />
                </div>
            ) : (
                <>
                    {/* Search Bar */}
                    <div className="max-w-5xl mx-auto w-full bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 shrink-0">
                        {/* Auto-fill Button Positioned here for context */}
                        <div className="flex justify-end mb-2">
                            <button
                                onClick={autoFillSearch}
                                disabled={autoFilling}
                                className="text-xs text-purple-600 hover:bg-purple-50 px-2 py-1 rounded transition-colors flex items-center gap-1"
                            >
                                {autoFilling ? <span className="animate-spin">✨</span> : <Sparkles size={12} />}
                                Auto-fill from Resume
                            </button>
                        </div>

                        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-3 text-slate-400" size={20} />
                                <input
                                    type="text"
                                    placeholder="Job Title, Keywords, or Company"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                            <div className="flex-1 relative">
                                <MapPin className="absolute left-3 top-3 text-slate-400" size={20} />
                                <input
                                    type="text"
                                    placeholder="Location (City, State, Zip, or 'Remote')"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-bold transition-colors disabled:opacity-50"
                            >
                                {loading ? 'Searching...' : 'Find Jobs'}
                            </button>
                        </form>
                        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                        {!localStorage.getItem('rapidapi_key') && (
                            <p className="text-amber-600 text-xs mt-2">
                                * Requires RapidAPI Key (JSearch) in Settings.
                            </p>
                        )}
                        {!isPremium && <AdBanner slotId={process.env.REACT_APP_ADSENSE_SLOT_ID || "1234567890"} style={{ marginTop: '1rem' }} />}
                    </div>

                    {/* Results Area */}
                    <div className="flex-1 max-w-5xl mx-auto w-full overflow-y-auto pr-2 pb-10">
                        {jobs.length === 0 && !loading && !error && (
                            <div className="text-center py-20 text-slate-400">
                                <Search size={48} className="mx-auto mb-4 opacity-20" />
                                <p>Enter keywords to start searching context-aware jobs.</p>
                            </div>
                        )}

                        <div className="grid gap-4">
                            {jobs.map((job) => (
                                <div key={job.job_id} className="bg-white p-5 rounded-xl border border-slate-200 hover:shadow-md transition-shadow relative overflow-hidden group">

                                    {/* Premium Blur Overlay for non-premium users */}
                                    {!isPremium && (
                                        <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Lock className="text-slate-800 mb-2" size={32} />
                                            <p className="font-bold text-slate-800 mb-2">Premium Feature</p>
                                            <button
                                                onClick={upgradeToPro}
                                                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-2 rounded-full font-bold shadow-lg hover:from-amber-600 hover:to-orange-600 transform hover:scale-105 transition-all"
                                            >
                                                Upgrade to Unlock
                                            </button>
                                        </div>
                                    )}

                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <h3 className="text-lg font-bold text-slate-800">{job.job_title}</h3>
                                                <TrustBadge job={job} />
                                            </div>
                                            <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
                                                <span className="font-medium">{job.employer_name}</span>
                                                <span>•</span>
                                                <span>{job.job_city || job.job_country || "Remote"}</span>
                                                {job.job_is_remote && <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">Remote</span>}
                                            </div>
                                            <p className="text-slate-500 text-sm line-clamp-2 mb-4">
                                                {job.job_description}
                                            </p>
                                        </div>
                                        {job.employer_logo && (
                                            <img src={job.employer_logo} alt="Logo" className="w-12 h-12 object-contain rounded-md" />
                                        )}
                                    </div>

                                    <div className="flex items-center gap-3 mt-auto pt-4 border-t border-slate-100">
                                        <a
                                            href={job.job_apply_link}
                                            target="_blank"
                                            rel="noreferrer"
                                            className={`flex-1 text-center py-2 rounded-lg font-medium transition-colors ${!isPremium ? 'text-slate-400 bg-slate-100 cursor-not-allowed pointer-events-none' : 'text-blue-600 bg-blue-50 hover:bg-blue-100'}`}
                                        >
                                            Apply Now <ExternalLink size={14} className="inline ml-1" />
                                        </a>
                                        <button
                                            onClick={() => isPremium && onAnalyzeJob({
                                                title: job.job_title,
                                                company: job.employer_name,
                                                description: job.job_description,
                                                link: job.job_apply_link,
                                                recruiterId: null // or whatever
                                            })}
                                            className={`flex-1 text-center py-2 rounded-lg font-medium transition-colors border flex items-center justify-center gap-2 ${!isPremium
                                                ? 'text-slate-400 border-slate-200 cursor-not-allowed pointer-events-none'
                                                : 'text-slate-700 border-slate-300 hover:bg-slate-50'
                                                }`}
                                        >
                                            <Sparkles size={16} className="text-purple-600" />
                                            Analyze Match
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div >
    );
};

export default JobFinder;
